import randomId from 'javascripts/utils/random-id';
import { setAttrs, setClassName } from 'javascripts/globals/slider-extensions';

export class Pagination {
  constructor($el, options) {
    this.$el = $el;

    this.initPagination();

    this.page = 1;
    this.pages = 1;
    this.items = 1;
    this.onPageChange = (() => {});
    this.onPerPageChange = (() => {});
    this.loop = false;

    this.set(options);

    this.bindEvents();
  }

  set(options) {
    if (options.page !== undefined) this.page = options.page;
    if (options.pages !== undefined) this.pages = options.pages;
    if (options.items !== undefined) this.items = options.items;
    if (options.onPageChange !== undefined) this.onPageChange = options.onPageChange;
    if (options.onPerPageChange !== undefined) this.onPerPageChange = options.onPerPageChange;
    if (options.loop !== undefined) this.loop = options.loop;

    this.updateTotal();
    this.updatePerPage();
    this.updatePagination(this.page);
  }

  initPagination() {
    const $pagination = this.$el.querySelector('.pagination');
    this.$navButtonPrev = $pagination.querySelector('.pagination__item--prev a');
    this.$navButtonNext = $pagination.querySelector('.pagination__item--next a');

    const $perPageLinks = $pagination.querySelectorAll('.pagination__setting a');
    this.perPageObjs = Array.from($perPageLinks).map($perPageLink => ({
      $perPageLink,
      $perPageItem: $perPageLink.parentElement,
      perPage: parseInt($perPageLink.getAttribute('data-per-page') || '0', 10),
    }));

    const $paginationSummary = $pagination.querySelector('.pagination__summary');
    [this.$pageCurrent] = $paginationSummary.children[0].childNodes;
    [this.$pageTotal] = $paginationSummary.children[2].childNodes;

    this.$pagination = $pagination;
  }

  updateTotal() {
    this.$pageTotal.textContent = this.pages;

    setClassName(this.$pagination, 'pagination--single-page', this.pages === 1);
  }

  updatePerPage() {
    this.$pageTotal.textContent = this.pages;

    const anyVisible = this.perPageObjs.map(({ $perPageItem, perPage }) => {
      const hidden = perPage > this.items;
      setClassName($perPageItem, 'pagination__setting--hidden', hidden);

      return !hidden;
    }).some(isVisible => isVisible);

    setClassName(this.$pagination, 'pagination--no-per-page', !anyVisible);
  }

  bindEvents() {
    this.$navButtonPrev.addEventListener('click', this.onPrevClick.bind(this));
    this.$navButtonNext.addEventListener('click', this.onNextClick.bind(this));

    this.onPerPageLinkClickEvent = this.onPerPageLinkClick.bind(this);
    this.perPageObjs.forEach(({ $perPageLink }) => {
      $perPageLink.addEventListener('click', this.onPerPageLinkClickEvent);
    });
  }

  onPerPageLinkClick(e) {
    e.preventDefault();

    const $currentPerPage = e.currentTarget;
    const perPage = parseInt($currentPerPage.getAttribute('data-per-page') || '-1', 10);

    this.perPageObjs.forEach(({ $perPageLink }) => {
      setClassName($perPageLink, 'link--no-underline', $perPageLink !== $currentPerPage);
    });

    this.onPerPageChange(perPage);
  }

  onPrevClick(e) {
    e.preventDefault();
    this.goTo(this.page - 1);
  }

  onNextClick(e) {
    e.preventDefault();
    this.goTo(this.page + 1);
  }

  goTo(page) {
    if (page === this.page) return;

    let newPage = page;
    if (newPage < 1) newPage = this.pages;
    if (newPage > this.pages) newPage = 0;

    this.page = newPage;

    this.updatePagination(this.page);
    this.onPageChange(this.page, this.pages);
  }

  updatePagination(page) {
    const { prev, next } = Pagination.getButtonAttrs(page, this.pages, this.loop);

    this.setNavButtonAttrs(this.$navButtonPrev, prev);
    this.setNavButtonAttrs(this.$navButtonNext, next);

    this.$pageCurrent.textContent = page;
  }

  setNavButtonAttrs($navButton, { disabled }) {
    setAttrs($navButton, {
      disabled: disabled ? 'disabled' : undefined,
      'aria-disabled': disabled ? 'true' : undefined,
    });
  }

  static getButtonAttrs(page, pages, loop = true) {
    return {
      prev: {
        disabled: !loop && page === 1,
      },
      next: {
        disabled: !loop && page === pages,
      },
    };
  }
}

export default class ImageVideoTiles {
  constructor($el, options) {
    this.$el = $el;
    this.id = options.id || `image-video-tiles${randomId()}`;

    this.page = 1;
    this.perPage = parseInt($el.getAttribute('data-tiles-per-page') || '12', 10);
    // this.items = JSON.parse($el.getAttribute('data-tiles-items') || '[]');
    this.$items = Array.from($el.querySelectorAll('.image-video-tiles__item'));
    this.itemsCount = this.$items.length;
    this.pages = Math.ceil(this.itemsCount / this.perPage);

    this.hiddenItemClass = 'image-video-tiles__item--hidden';

    this.initPagination();
    this.updatePage();
  }

  initPagination() {
    this.pagination = new Pagination(this.$el, {
      page: this.page,
      pages: this.pages,
      items: this.itemsCount,
      onPageChange: this.onPageChange.bind(this),
      onPerPageChange: this.onPerPageChange.bind(this),
    });
  }

  onPageChange(page) {
    if (page < 0) return;
    if (page > this.pages) return;
    this.page = page;

    this.updatePage();
  }

  onPerPageChange(perPage) {
    if (perPage <= 0) return;
    this.perPage = perPage;
    this.pages = Math.ceil(this.itemsCount / this.perPage);
    this.page = 1;

    this.pagination.set({
      page: this.page,
      pages: this.pages,
    });

    this.updatePage();
  }

  updatePage() {
    const startIndex = (this.page - 1) * this.perPage;
    const endIndex = startIndex + this.perPage - 1;

    this.$items.forEach(($item, index) => {
      setClassName($item, this.hiddenItemClass, index < startIndex || index > endIndex);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.js-image-video-tiles').forEach(
    $imageVideoTiles => new ImageVideoTiles($imageVideoTiles, {}),
  );
});
